/**
 * useSupabaseQuery — Generic reactive Supabase query hook
 *
 * Provides: data, loading, error, refetch
 * Auto-fetches on mount and when deps change.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getErrorMessage } from '../utils/errors';

export type QueryState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

export type UseQueryResult<T> = QueryState<T> & {
  refetch: () => Promise<void>;
};

export type QueryFn<T> = () => PromiseLike<{ data: T | null; error: { message: string } | null }>;

export function useSupabaseQuery<T>(
  queryFn: QueryFn<T>,
  deps: unknown[] = [],
  options?: { enabled?: boolean },
): UseQueryResult<T> {
  const enabled = options?.enabled ?? true;
  const [state, setState] = useState<QueryState<T>>({
    data: null,
    loading: enabled,
    error: null,
  });
  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    const id = ++fetchIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await queryFn();
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      if (error) {
        setState({ data: null, loading: false, error: error.message });
      } else {
        setState({ data, loading: false, error: null });
      }
    } catch (err) {
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setState({ data: null, loading: false, error: getErrorMessage(err) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      // Bump fetchId to invalidate any in-flight request
      fetchIdRef.current++;
      setState((prev) => prev.loading ? { ...prev, loading: false } : prev);
      return () => { mountedRef.current = false; };
    }
    fetch();
    return () => {
      mountedRef.current = false;
    };
  }, [fetch, enabled]);

  return { ...state, refetch: fetch };
}